<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PostSaleManagementDeedTimeline from './PostSaleManagementDeedTimeline.vue';
import PostSaleManagementDeedPerson from './PostSaleManagementDeedPerson.vue';

const props = defineProps({ row: { type: Object, required: true } });
const emit = defineEmits(['openCase']);
const { t } = useI18n();
const expanded = ref(false);

const address = computed(() => {
  const firstLine = [
    props.row.logradouro,
    props.row.numero && `, ${props.row.numero}`,
    props.row.unidade && ` / ${props.row.unidade}`,
    props.row.complemento && ` - ${props.row.complemento}`,
  ]
    .filter(Boolean)
    .join('');
  const secondLine = [
    props.row.bairro,
    props.row.cidade && ` - ${props.row.cidade}`,
    props.row.uf && ` - ${props.row.uf}`,
  ]
    .filter(Boolean)
    .join('');
  return [firstLine, secondLine].filter(Boolean);
});
const payments = computed(() =>
  (props.row.forma_pagamento || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
);
</script>

<template>
  <tr
    class="border-t border-lais-border-soft align-top text-sm text-lais-text-body"
  >
    <td class="px-4 py-4">
      <PostSaleManagementDeedPerson :name="row.nome_resposavel" />
    </td>
    <td class="px-4 py-4">
      <p class="font-medium text-lais-text">
        {{ address[0] || t('POST_SALE.COMMON.ADDRESS_UNAVAILABLE') }}
      </p>
      <p v-if="address[1]" class="mt-1 text-xs text-lais-text-muted">
        {{ address[1] }}
      </p>
    </td>
    <td class="px-4 py-4">
      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="payment in payments"
          :key="payment"
          class="rounded-full bg-lais-bg-soft px-2 py-1 text-xs font-medium text-lais-text-body"
        >
          {{ payment }}
        </span>
        <span v-if="!payments.length" class="text-lais-text-muted">{{
          t('POST_SALE.COMMON.EMPTY_VALUE')
        }}</span>
      </div>
    </td>
    <td class="px-4 py-4 text-center font-semibold text-lais-text">
      {{ row.todos_status.total_dias }}
    </td>
    <td class="px-4 py-4">
      <PostSaleManagementDeedPerson :name="row.nome_gerente" />
    </td>
    <td class="px-4 py-4 text-right">
      <button
        type="button"
        class="grid size-8 place-items-center rounded-full text-lais-primary hover:bg-lais-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
        :aria-label="t('POST_SALE.MANAGEMENT.DEEDS_TOGGLE_TIMELINE')"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      >
        <span
          class="size-4"
          :class="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          aria-hidden="true"
        />
      </button>
    </td>
  </tr>
  <tr v-if="expanded" class="border-t border-lais-border-soft bg-lais-bg-soft">
    <td colspan="6" class="px-4 py-4">
      <p class="text-sm font-medium text-lais-text">
        {{ t('POST_SALE.MANAGEMENT.DEEDS_DAYS_BY_STATUS') }}
      </p>
      <PostSaleManagementDeedTimeline
        class="mt-3"
        :post-sale-case-id="row.processo_id"
        :statuses="row.todos_status.status"
        @open-case="emit('openCase', $event)"
      />
      <p class="mt-4 text-sm text-lais-text-muted">
        {{
          t('POST_SALE.MANAGEMENT.DEEDS_TOTAL_DAYS', {
            days: row.todos_status.total_dias,
          })
        }}
      </p>
    </td>
  </tr>
</template>
